/**
 * Resolves external partner_id + partner_property_id query params to internal
 * property selection params using the /me session mapping. Pure function for testability.
 */

import { PROPERTY_SELECTION_URL_PARAM } from '@/lib/property-select/property-selection-storage';

const PARTNER_ID = 'partner_id';
const PARTNER_PROPERTY_ID = 'partner_property_id';

export type PartnerPropertyMap = Record<string, Record<string, string>>;

/**
 * Given current location and the business_partner_property_to_surface_property_map from /me,
 * returns the full resolved URL (pathname + search + hash) with partner_property_id replaced by
 * `property_id`, or null if no resolution is needed or possible.
 *
 * - Keeps partner_id in the URL.
 * - Removes all partner_property_id from the URL.
 * - Adds `property_id` for each partner_property_id that exists in the map (unresolved IDs are omitted).
 * - Preserves hash fragment if present in location.
 * - When multiple partner_property_id values are present, resolves each that is in the map.
 */
export function resolvePartnerParamsToResolvedUrl(
  location: { pathname: string; search: string; hash?: string },
  map: PartnerPropertyMap
): string | null {
  const params = new URLSearchParams(location.search);
  const partnerId = params.get(PARTNER_ID);
  const partnerPropertyIds = params.getAll(PARTNER_PROPERTY_ID);

  if (!partnerId || partnerPropertyIds.length === 0) {
    return null;
  }

  const partnerMap = map[partnerId];
  const resolvedIds = partnerPropertyIds
    .map((ppId) => partnerMap?.[ppId])
    .filter((id): id is string => id != null);

  if (resolvedIds.length === 0) {
    return null;
  }

  const next = new URLSearchParams(params);
  next.delete(PARTNER_PROPERTY_ID);
  for (const resolvedId of resolvedIds) {
    next.append(PROPERTY_SELECTION_URL_PARAM, resolvedId);
  }
  const search = next.toString();
  const pathAndSearch = search
    ? `${location.pathname}?${search}`
    : location.pathname;
  const hash = location.hash ?? '';
  return `${pathAndSearch}${hash}`;
}
